import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";

type AddChaptersSidebarProps = {
    onBack: () => void;
    onSubmit?: (count: number | null) => void;
    onApply?: () => void;
    isLoading?: boolean;
    error?: string | null;
    draft?: Array<{ title: string; timeSec: number; snippet: string }>;
};

const formatMMSS = (seconds: number): string => {
    const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const truncateSnippet = (value: string, maxChars: number): string => {
    const cleaned = String(value ?? "").replace(/\s+/g, " ").trim();
    if (!cleaned) return "";
    if (cleaned.length <= maxChars) return cleaned;
    return `${cleaned.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;
};

const AddChaptersSidebar: React.FC<AddChaptersSidebarProps> = ({
    onBack,
    onSubmit,
    onApply,
    isLoading,
    error,
    draft,
}) => {
    const [value, setValue] = useState<string>("");
    const hasDraft = Array.isArray(draft) && draft.length > 0;
    const primaryButtonLabel = hasDraft ? "Regenerate" : "Generate";

    const draftRows = useMemo(() => {
        if (!hasDraft) return [];
        return (draft ?? []).map((d, idx) => ({
            key: `${idx}-${String(d?.title ?? "")}`,
            title: String(d?.title ?? "").trim() || "Marker",
            time: formatMMSS(Number(d?.timeSec ?? 0)),
            snippet: truncateSnippet(String(d?.snippet ?? ""), 140),
        }));
    }, [draft, hasDraft]);

    const normalizeValue = (raw: string): string => {
        const trimmed = raw.trim();
        if (!trimmed) return "";
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed)) return "";
        const clamped = Math.max(2, Math.floor(parsed));
        return String(clamped);
    };

    const handleBlur = () => {
        setValue((prev) => normalizeValue(prev));
    };

    const handleSubmit = () => {
        const normalized = normalizeValue(value);
        setValue(normalized);
        const count = normalized ? Number(normalized) : null;
        onSubmit?.(count);
    };

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                p: "3px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "8px" }}>
                <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                    <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
                    Add chapters
                </Typography>
            </Box>

            <Box
                sx={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                }}
            >
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Divide into chapters & add markers
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        padding: "8px 10px",
                    }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#1F2937" }}>
                        Number
                    </Typography>
                    <TextField
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onBlur={handleBlur}
                        placeholder="Auto"
                        size="small"
                        variant="outlined"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={{
                            width: 80,
                            '& .MuiInputBase-input': {
                                textAlign: "right",
                                fontSize: 13,
                                fontWeight: 500,
                                color: "#1F2937",
                            },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: "#FFFFFF",
                                borderRadius: "8px",
                                '& fieldset': {
                                    borderColor: "#E5E7EB",
                                },
                                '&:hover fieldset': {
                                    borderColor: "#D1D5DB",
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: "#2B6BFD",
                                },
                            },
                        }}
                    />
                </Box>

                <Button
                    data-tour="chapters-generate"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={Boolean(isLoading)}
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        textTransform: "none",
                        color: "#FFFFFF",
                        backgroundColor: "#1A1A1A",
                        borderRadius: "8px",
                        height: "40px",
                        "&:hover": {
                            backgroundColor: "#2B2B2B",
                        },
                        "&:disabled": {
                            backgroundColor: "#E5E7EB",
                            color: "#9CA3AF",
                        },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        {isLoading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
                        <Box>{primaryButtonLabel}</Box>
                    </Box>
                </Button>

                {!!error && (
                    <Box
                        sx={{
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            borderRadius: "10px",
                            px: "10px",
                            py: "8px",
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "#991B1B" }}>
                            {error}
                        </Typography>
                    </Box>
                )}
            </Box>

            {hasDraft ? (
                <Box
                    sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: "12px",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                            Preview
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
                            {draftRows.length} {draftRows.length === 1 ? "chapter" : "chapters"}
                        </Typography>
                    </Box>
                    <Divider sx={{ borderColor: "#E5E7EB" }} />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "auto" }}>
                        {draftRows.map((row) => (
                            <Box
                                key={row.key}
                                sx={{
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "10px",
                                    px: "10px",
                                    py: "9px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: "#111827",
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {row.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12.5, color: "#6B7280", flexShrink: 0 }}>
                                        {row.time}
                                    </Typography>
                                </Box>
                                {row.snippet ? (
                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            color: "#6B7280",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {row.snippet}
                                    </Typography>
                                ) : null}
                            </Box>
                        ))}
                    </Box>

                    <Button
                        data-tour="chapters-apply"
                        fullWidth
                        onClick={onApply}
                        disabled={Boolean(isLoading) || !onApply}
                        sx={{
                            mt: "2px",
                            fontFamily: "Inter",
                            fontSize: "14px",
                            fontWeight: 500,
                            textTransform: "none",
                            color: "#FFFFFF",
                            backgroundColor: "#1A1A1A",
                            borderRadius: "8px",
                            height: "40px",
                            "&:hover": {
                                backgroundColor: "#2B2B2B",
                            },
                            "&:disabled": {
                                backgroundColor: "#E5E7EB",
                                color: "#9CA3AF",
                            },
                        }}
                    >
                        Add to script
                    </Button>
                </Box>
            ) : null}
        </Box>
    );
};

export default AddChaptersSidebar;
